import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { colors, typography, spacing, media } from "../styles/designSystem";
import { PageHeadline, SectionHeadline, TerminalLabel } from "../components/Typography";
import NDADisclosure from "../components/NDADisclosure";
import { PipelineFlow, DomainGrid, ContrastPair } from "../components/diagrams/primitives";
import { VariantVsProperty, TokenFlow } from "../components/SystemDiagram";

/**
 * System Design.
 *
 * The homepage states these arguments in summary; this page is the operational
 * version — what a shared system costs, how a component earns its way into one, and
 * where the boundaries fail. The worked example is this site, which is the only
 * system here a reader can actually inspect.
 */

const PageWrapper = styled.div`
    max-width: 920px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const Meta = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};
    margin: ${spacing[500]} 0 ${spacing[600]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 72ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Pull = styled.blockquote`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
`;

const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

const Ref = styled(Link)`
    color: ${({ theme }) => theme.primary};
    border-bottom: 1px dotted ${({ theme }) => theme.primary};
    white-space: nowrap;

    &:hover {
        background: ${({ theme }) => theme.primary};
        color: ${({ theme }) => theme.background};
        text-shadow: none;
        border-bottom-color: transparent;
    }
`;

/* The combinatorial argument is arithmetic, so it is shown as arithmetic rather than
   as a claim about how much better things got. */
const MathRow = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing[400]};
    margin: ${spacing[700]} 0;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const MathCell = styled.div`
    border: 1px solid ${({ color }) => color};
    border-top-width: 3px;
    padding: ${spacing[600]} ${spacing[500]};
    min-width: 0;
`;

const MathLabel = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[100]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ color }) => color};
    margin-bottom: ${spacing[400]};
`;

const MathLine = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    letter-spacing: ${typography.letterSpacing.normal};
    color: ${({ theme }) => theme.subtleText};
    line-height: ${typography.lineHeight[500]};

    strong {
        color: ${({ color }) => color};
        font-weight: ${typography.fontWeight.regular};
    }
`;

const LIFECYCLE = [
    {
        name: "Built in a product",
        desc: "The first version solves one real problem, in the application that needed it",
        color: colors.primary[500],
    },
    {
        name: "Built again",
        desc: "A second case arrives and the shape is copied — deliberately, not accidentally",
        color: colors.primary[500],
    },
    {
        name: "Extracted",
        desc: "Only now is the seam visible, because two real uses defined it instead of one guess",
        color: colors.cyan[300],
    },
    {
        name: "Given an API",
        desc: "Props, states, and composition points fixed as the contract consumers depend on",
        color: colors.amber[400],
    },
    {
        name: "Adopted",
        desc: "Existing usages migrate; a shared component nobody uses is pure overhead",
        color: colors.emerald[500],
    },
    {
        name: "Deprecated",
        desc: "Systems that cannot remove anything eventually stop being usable",
        color: colors.coral[400],
    },
];

const FAILURES = [
    {
        name: "State in the leaf",
        color: colors.coral[400],
        desc: "A component owning state its parent needed makes every new usage fight it.",
    },
    {
        name: "Fetching in the button",
        color: colors.coral[400],
        desc: "Data access below the block boundary ties presentation to an endpoint.",
    },
    {
        name: "The fork",
        color: colors.amber[400],
        desc: "A deadline met by copying a component, after which two truths exist.",
    },
    {
        name: "Boolean drift",
        color: colors.amber[400],
        desc: "Flags added one at a time until no one can say what a valid combination is.",
    },
    {
        name: "The one-off value",
        color: colors.cyan[300],
        desc: "A hardcoded colour that survives every future retheme untouched.",
    },
    {
        name: "The ownerless pattern",
        color: colors.purple[400],
        desc: "Shared by everyone, maintained by nobody, and trusted by no one after a year.",
    },
];

const SystemDesign = () => (
    <PageWrapper>
        <Header>
            <PageHeadline>System Design</PageHeadline>
            <Meta>
                <TerminalLabel>Position</TerminalLabel>
                <span>A design system is an API with several consumers and a blast radius</span>
            </Meta>
            <Lede>
                A component library is a folder. A system is a set of contracts other people build
                against, which means the interesting questions are not what things look like but{" "}
                <strong>
                    what is allowed to change, who finds out when it does, and what it costs to be
                    wrong
                </strong>
                . The homepage states these arguments briefly. This is the operational version.
            </Lede>
        </Header>

        <Section>
            <SectionHeadline>Variants Multiply, Properties Add</SectionHeadline>
            <Para>
                The most common way an enterprise system becomes unmaintainable is not neglect. It is
                modelling appearance as separate components, which turns every new visual dimension
                into a multiplication rather than an addition.
            </Para>
            <MathRow>
                <MathCell color={colors.coral[400]}>
                    <MathLabel color={colors.coral[400]}>Variants as components</MathLabel>
                    <MathLine color={colors.coral[400]}>
                        2 styles &times; 2 sizes &times; 2 states = <strong>8 components</strong>
                        <br />
                        add an icon flag = <strong>16 components</strong>
                        <br />
                        each one a place a bug can diverge
                    </MathLine>
                </MathCell>
                <MathCell color={colors.emerald[500]}>
                    <MathLabel color={colors.emerald[500]}>Variants as properties</MathLabel>
                    <MathLine color={colors.emerald[500]}>
                        1 component, 3 props = <strong>1 implementation</strong>
                        <br />
                        add an icon flag = <strong>1 implementation</strong>
                        <br />
                        permutations live in the type signature
                    </MathLine>
                </MathCell>
            </MathRow>
            <Para>
                That is arithmetic, not an opinion. The permutations do not disappear either way
                &mdash; the question is only whether they live in files a person has to maintain or
                in a signature the compiler checks and a consumer can discover.
            </Para>
            <DiagramSlot>
                <VariantVsProperty />
            </DiagramSlot>
        </Section>

        <Section>
            <SectionHeadline>Three Layers, Deliberately</SectionHeadline>
            <Para>
                Raw values are defined once. Semantic decisions map those values to purposes.
                Components consume the decisions and never reach past them. The point of the middle
                layer is blast radius: changing a value has a known set of consequences instead of an
                unknown one.
            </Para>
            <DiagramSlot>
                <TokenFlow />
            </DiagramSlot>
            <Para>
                This site is the test case. Retargeting the entire palette to a terminal phosphor was
                a change to the token layer, and every page inherited it without being edited &mdash;
                because none of them had been allowed to name a colour directly. A theme here is a
                different set of decisions pointing at the same values, not a parallel stylesheet.
            </Para>
            <Pull>
                A token layer is not a naming exercise. It is the difference between a rebrand and a
                rewrite.
            </Pull>
            <Para>
                It also has a limit worth being honest about. Some decisions cannot be expressed as a
                value at all &mdash; this site removes rounded corners globally with a single rule
                scoped to the app root, because roughly two dozen components had set their own
                radius and one rounded card breaks the illusion. When a decision cannot live in the
                token layer, it needs enforcement instead, and pretending otherwise is how systems
                quietly stop being true.
            </Para>
        </Section>

        <Section>
            <SectionHeadline>How a Component Earns Its Way In</SectionHeadline>
            <Para>
                Shared components should be extracted, not designed up front. An abstraction built
                before the second real use case encodes a guess, and every consumer afterwards pays
                to work around it.
            </Para>
            <DiagramSlot>
                <PipelineFlow
                    label="The Lifecycle of a Shared Component"
                    stages={LIFECYCLE}
                    caption="The diagram vocabulary on this site followed exactly this path: four case studies built their own diagrams first, and only the shapes that appeared in all four were extracted into a shared module."
                />
            </DiagramSlot>
        </Section>

        <Section>
            <SectionHeadline>Where the Boundaries Fail</SectionHeadline>
            <Para>
                Systems rarely collapse at once. They erode through a small number of recurring
                mistakes, each of which is cheap on the day it happens and expensive for years
                afterwards.
            </Para>
            <DiagramSlot>
                <DomainGrid label="The Recurring Failures" domains={FAILURES} />
            </DiagramSlot>
            <Para>
                Every one of these is the same underlying error &mdash; a decision placed at the
                wrong level. Getting the level right is most of what component architecture actually
                is.
            </Para>
        </Section>

        <Section>
            <SectionHeadline>Naming for Machines</SectionHeadline>
            <Para>
                Names like <strong>primary</strong> and <strong>secondary</strong> carry design
                intent that only a person who was in the room can decode. Functional names &mdash;{" "}
                <strong>default</strong>, <strong>outline</strong>, <strong>ghost</strong> &mdash;
                describe what the component does, which makes prop values self-documenting and
                readable by something that was not in the room.
            </Para>
            <Para>
                That mattered before AI and matters more now. A property-based component can be
                instantiated from a structured object; a variant-based one requires knowing which of
                several hundred component names is the right one. At{" "}
                <Ref to="/work/att">AT&amp;T</Ref> this was the whole problem: one design system
                consumed by designers in Figma, engineers in a component library, and agents in the
                repository, where consistency had to be enforceable rather than requested.
            </Para>
            <ContrastPair
                left={{
                    label: "Intent naming",
                    color: colors.coral[400],
                    text: "primary / secondary — meaningful only to whoever chose it",
                }}
                right={{
                    label: "Functional naming",
                    color: colors.emerald[500],
                    text: "default / outline / ghost — meaningful to anything reading the API",
                }}
            />
        </Section>

        <Section>
            <SectionHeadline>What a System Costs</SectionHeadline>
            <Para>
                A shared system is overhead until it is reused, and it stays overhead if adoption is
                assumed rather than done. That is the honest counterweight to everything above:
                extraction has a price, governance has a price, and a system nobody migrates onto is
                a second codebase rather than a foundation.
            </Para>
            <Para>
                So the test I apply is narrow. Does this remove a decision someone would otherwise
                have to make again? Can its rules be checked by something other than a person
                remembering them? And is there a path for the usages that already exist? If the
                answer to any of those is no, the abstraction is not ready &mdash; regardless of how
                clean it looks.
            </Para>
            <Pull>
                Consistency that depends on everyone remembering is not consistency. It is a
                coincidence with good intentions.
            </Pull>
        </Section>

        <Section>
            <SectionHeadline>Built, Not Described</SectionHeadline>
            <Para>
                These arguments were built into production systems at{" "}
                <Ref to="/work/att">AT&amp;T</Ref>, <Ref to="/work/Verizon">Verizon</Ref>,{" "}
                <Ref to="/work/spectrum">Spectrum</Ref>, and{" "}
                <Ref to="/work/NsaBrands">NSA Brands</Ref> &mdash; and they are running underneath
                this page right now.
            </Para>
            <Para>
                The <Ref to="/components">component registry</Ref> renders the real exported
                components from this site rather than screenshots of them, so what you see there is
                the implementation and not a picture of it. If the API were wrong, that page would be
                the first place it showed.
            </Para>
        </Section>

        <NDADisclosure />
    </PageWrapper>
);

export default SystemDesign;
